import { useEffect, useRef } from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon
} from "@heroicons/react/20/solid";
import Button from "../../components/atoms/button";
import toast, { Toast } from "react-hot-toast";
import { cn } from "../../utils/cn";
import { MAX_SAFEST_INTEGER } from "../../utils/constants";
import { getAuthoringLibraryLabel } from "@lxp-app/utils/authoringLibraryResources/localizeAuthoringLibraryLabels";
import { useSelector } from "react-redux";
import { useAuthoringLibraryLanguage } from "@lxp-app/utils/authoringLibraryResources/useAuthoringLibraryLanguage";

interface Props {
  severity: "error" | "warning" | "success" | "info";
  label: string;
  description?: string;
  messageList?: any[];
  autoHideDuration?: number;
  timeoutCallback?: () => void;
  actionButtonClick?: () => void;
  isNotification?: boolean;
}

function ErrorListAlerts({
  severity,
  label,
  description,
  messageList,
  isNotification = false,
  autoHideDuration: duration = severity == "success"
    ? 5000
    : MAX_SAFEST_INTEGER,
  timeoutCallback,
  actionButtonClick
}: Props) {
  const ref = useRef(false);
  const user = useSelector((state: any) => state.UserInfoSlice);
  const { language: selectedLanguage } = useAuthoringLibraryLanguage(
    user?.profile?.preferences?.language
  );

  const Custom = ({ t }: { t: Toast }) => {
    // useEffect(() => {
    //   const timeout = setTimeout(() => {
    //     toast.dismiss(t.id);
    //     if (timeoutCallback) {
    //       timeoutCallback();
    //     }
    //   }, t.duration);

    //   return () => {
    //     clearTimeout(timeout);
    //   };
    // }, []);

    return (
      <div>
        <div
          className={cn(
            `flex`,
            severity === "success" && "text-green-800",
            severity === "error" && "text-red-800",
            severity === "warning" && "text-yellow-800",
            severity === "info" && "text-blue-800"
          )}
          // className={cn("flex", severity === "success" && )}
        >
          <div className="mr-2">
            {severity === "error" ? (
              <XCircleIcon
                className="h-5 w-5 text-red-400"
                aria-hidden="true"
              />
            ) : severity === "info" ? (
              <InformationCircleIcon
                className="h-5 w-5 text-blue-400"
                aria-hidden="true"
              />
            ) : severity === "warning" ? (
              <ExclamationTriangleIcon
                className="h-5 w-5 text-yellow-400"
                aria-hidden="true"
              />
            ) : (
              <CheckCircleIcon
                className="h-5 w-5 text-green-400"
                aria-hidden="true"
              />
            )}
          </div>
          <div>
            <label className={`text-sm font-medium`}>{label}</label>
            {description ? (
              <span className="text-sm">{description}</span>
            ) : (
              <p className="text-sm">{description}</p>
            )}
            {messageList && (
              <ul>
                {messageList.map((msg, id) => (
                  <li
                    key={id}
                    className={
                      messageList.length > 1
                        ? "text-sm list-outside list-disc"
                        : "text-sm"
                    }
                    style={{
                      listStyleType: "disc",
                      color:
                        severity === "error"
                          ? "text-red-800"
                          : severity === "info"
                            ? "text-blue-700"
                            : severity === "warning"
                              ? "text-yellow-700"
                              : "text-green-800"
                    }}
                  >
                    {msg}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="w-full my-1 ml-4 inline-flex justify-end pr-3">
          <Button
            label={getAuthoringLibraryLabel("dismiss", selectedLanguage)}
            variant="default"
            className={cn(
              `py-1 px-3 rounded-md`,
              severity === "error" &&
                "text-red-800 bg-red-50 hover:bg-red-300 focus:ring-red-500  focus:ring-offset-red-50",
              severity === "info" &&
                "text-blue-700 bg-blue-50 hover:bg-blue-200 focus:ring-blue-500  focus:ring-offset-blue-50",
              severity === "warning" &&
                "text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:ring-yellow-500  focus:ring-offset-yellow-50",
              severity === "success" &&
                "bg-green-50  text-green-800 hover:bg-green-100  focus:ring-green-600  focus:ring-offset-green-50"
            )}
            onClick={() => {
              toast.dismiss(t.id);
              if (actionButtonClick) {
                actionButtonClick();
              }
            }}
            testId="dismissErrorListBtn"
          />
        </div>
      </div>
    );
  };

  const displayToast = () => {
    ref.current = true;
    return toast((t: Toast) => <Custom t={t} />, {
      style: {
        background:
          severity === "error"
            ? "#fef2f2"
            : severity === "info"
              ? "eff6ff"
              : severity === "warning"
                ? "#fefce8"
                : "#f0fdf4"
      },
      duration
    });
  };

  useEffect(() => {
    if (!ref.current) displayToast();
  }, [severity]);

  return null;
}

export default ErrorListAlerts;
